import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface IgAccount {
  id: string;
  igUserId: string;
  igUsername: string;
  profilePicUrl: string | null;
  status: "active" | "reauth_required" | "suspended" | "disconnected";
  tokenExpiresAt: string | null;
  createdAt: string;
}

interface RateLimiterState {
  rate: number;
  tokens: number;
  lastPenaltyMs: number | null;
  defaultRate: number;
  maxRate: number;
}

type ConnectStatus =
  | { kind: "ok"; username: string }
  | { kind: "denied" }
  | { kind: "error"; reason: string };

export interface SearchParams {
  ig_connect?: "ok" | "denied" | "error";
  username?: string;
  reason?: string;
  detail?: string;
}

export const Route = createFileRoute("/_app/settings/instagram")({
  component: InstagramSettings,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    ig_connect: search.ig_connect as SearchParams["ig_connect"],
    username: typeof search.username === "string" ? search.username : undefined,
    reason: typeof search.reason === "string" ? search.reason : undefined,
    detail: typeof search.detail === "string" ? search.detail : undefined,
  }),
});

function InstagramSettings() {
  const { workspace } = useAuth();
  const search = useSearch({ from: "/_app/settings/instagram" });
  const [accounts, setAccounts] = useState<IgAccount[]>([]);
  const [rateState, setRateState] = useState<Record<string, RateLimiterState>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const banner: ConnectStatus | null =
    search.ig_connect === "ok" && search.username
      ? { kind: "ok", username: search.username }
      : search.ig_connect === "denied"
        ? { kind: "denied" }
        : search.ig_connect === "error"
          ? { kind: "error", reason: search.reason ?? "graph_error" }
          : null;

  useEffect(() => {
    void load();
  }, [workspace?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ accounts: IgAccount[] }>("/ig/accounts");
      setAccounts(res.data.accounts);
      // Fan out to fetch rate-limiter state for active accounts only.
      // Disconnected/suspended accounts won't have a meaningful bucket.
      const active = res.data.accounts.filter((a) => a.status === "active");
      const states = await Promise.all(
        active.map(async (a) => {
          try {
            const r = await api.get<RateLimiterState>(
              `/ig/accounts/${a.id}/rate-limiter-state`,
            );
            return [a.id, r.data] as const;
          } catch {
            return null;
          }
        }),
      );
      const next: Record<string, RateLimiterState> = {};
      for (const entry of states) if (entry) next[entry[0]] = entry[1];
      setRateState(next);
    } catch {
      setError("Failed to load connected accounts.");
    } finally {
      setLoading(false);
    }
  }

  function handleConnect() {
    // Top-level navigation away — Meta needs to set + read the state cookie
    // on the api origin, which only works if the browser is going there
    // directly (no XHR).
    const apiBase =
      import.meta.env.VITE_API_URL ?? "http://localhost:3000";
    window.location.href = `${apiBase}/auth/ig/start`;
  }

  async function handleDisconnect(id: string) {
    if (
      !confirm(
        "Disconnect this Instagram account? Inbound messages will stop being received. You can Reconnect anytime to resume.",
      )
    ) {
      return;
    }
    setBusy(id);
    try {
      await api.delete(`/ig/accounts/${id}`);
      await load();
    } catch {
      setError("Disconnect failed. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleDeletePermanently(id: string, igUsername: string) {
    if (
      !confirm(
        `Permanently delete @${igUsername}? This will remove all messages, conversations, and contacts for this account. This cannot be undone.`,
      )
    ) {
      return;
    }
    if (!confirm(`Really delete @${igUsername}? Type-confirm by clicking OK once more.`)) {
      return;
    }
    setBusy(id);
    try {
      await api.delete(`/ig/accounts/${id}?permanent=true`);
      await load();
    } catch {
      setError("Delete failed. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>
        Instagram accounts
      </h1>
      <p style={{ color: "var(--muted)", marginBottom: 24 }}>
        Connect the Instagram Business accounts whose DMs you want to manage in OyeReply.
      </p>

      {banner?.kind === "ok" && (
        <div className="brut-card" style={{ background: "#e6f7e6", marginBottom: 24 }}>
          ✓ Connected <strong>@{banner.username}</strong>.
        </div>
      )}
      {banner?.kind === "denied" && (
        <div className="brut-card" style={{ background: "#fff8d8", marginBottom: 24 }}>
          You cancelled the connection. No changes were made.
        </div>
      )}
      {banner?.kind === "error" && (
        <div className="brut-error">
          <div>Connection failed: {humanizeReason(banner.reason)}</div>
          {search.detail && (
            <div style={{ fontWeight: 400, marginTop: 6, fontSize: 13 }}>
              Meta said: <em>{search.detail}</em>
            </div>
          )}
        </div>
      )}
      {error && <div className="brut-error">{error}</div>}

      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <button className="brut-button" onClick={handleConnect}>
          Connect Instagram
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : accounts.length === 0 ? (
        <div className="brut-card">
          <p style={{ margin: 0 }}>No Instagram accounts connected yet.</p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {accounts.map((a) => (
            <li key={a.id} className="brut-card" style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {a.profilePicUrl ? (
                <img
                  src={a.profilePicUrl}
                  alt=""
                  width={56}
                  height={56}
                  style={{ border: "var(--border)", borderRadius: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    border: "var(--border)",
                    background: "var(--accent)",
                  }}
                />
              )}
              <div style={{ flex: 1, opacity: a.status === "disconnected" ? 0.6 : 1 }}>
                <div style={{ fontWeight: 700 }}>@{a.igUsername}</div>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  Status: {a.status}
                  {a.status === "reauth_required" && " — click Reconnect to refresh access"}
                  {a.status === "disconnected" && " — Reconnect to resume, or delete permanently"}
                </div>
                {a.status === "active" && rateState[a.id] && (
                  <RateBadge state={rateState[a.id]!} />
                )}
              </div>
              {a.status === "active" ? (
                <button
                  className="brut-button is-danger"
                  disabled={busy === a.id}
                  onClick={() => handleDisconnect(a.id)}
                >
                  {busy === a.id ? "…" : "Disconnect"}
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    className="brut-button"
                    disabled={busy === a.id}
                    onClick={handleConnect}
                    title="Re-runs OAuth and refreshes the long-lived token. The same row is reused."
                  >
                    Reconnect
                  </button>
                  <button
                    className="brut-button is-danger"
                    disabled={busy === a.id}
                    onClick={() => handleDeletePermanently(a.id, a.igUsername)}
                  >
                    {busy === a.id ? "…" : "Delete permanently"}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function RateBadge({ state }: { state: RateLimiterState }) {
  // Healthy = at or above the steady-state default. Penalized = halved
  // recently. The badge color flips when we're below default so a
  // glance tells the customer "we're throttling you because Meta did".
  const healthy = state.rate >= state.defaultRate;
  const ago = state.lastPenaltyMs ? formatAgo(Date.now() - state.lastPenaltyMs) : null;
  return (
    <div
      style={{
        marginTop: 6,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        color: healthy ? "var(--muted)" : "#a04400",
        background: healthy ? "transparent" : "#fff8e6",
        padding: "2px 8px",
        border: healthy ? "none" : "var(--border)",
      }}
      title={`Adaptive token bucket: ${state.rate.toFixed(2)}/sec (default ${state.defaultRate}/s, max ${state.maxRate}/s). Bucket has ${state.tokens.toFixed(1)} tokens.`}
    >
      <span>{state.rate.toFixed(1)} sends/sec</span>
      {ago && <span>· last rate-limited {ago}</span>}
    </div>
  );
}

function formatAgo(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function humanizeReason(reason: string): string {
  switch (reason) {
    case "state_mismatch":
      return "session mismatch (try again — don't open the connect link in a different browser tab)";
    case "already_connected":
      return "this Instagram account is already connected to a different OyeReply workspace";
    case "session_lost":
    case "session_expired":
      return "your session expired during the connect flow. Sign in and try again.";
    case "missing_params":
      return "Meta sent us back without enough info to continue. Try clicking Connect again.";
    case "meta_config_error":
      return "Meta blocked the OAuth — usually means the app's domains aren't configured to include this site. Forwarding the message Meta sent below.";
    case "graph_error":
    default:
      return "Meta returned an error. Confirm your IG account is a Business or Creator account linked to a Facebook Page.";
  }
}
